import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map as MapIcon } from 'lucide-react';
import { handleError, ERROR_CATEGORY } from '../../../utils/ErrorUtils';
import './Minimap.css';

const MINIMAP_SIZE = { width: 220, height: 160 };

const finiteOr = (value, fallback = 0) => (
  Number.isFinite(Number(value)) ? Number(value) : fallback
);

const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(value, maximum));

/**
 * BattleRoom map overview and camera navigator.
 *
 * All map content, tokens, and the viewport use the same contained transform.
 * `viewport` is already expressed in map/world coordinates by BattleRoom; zoom
 * must not be applied to it a second time here.
 */
const Minimap = ({
  mapImage = null,
  tokens = [],
  viewport = { x: 0, y: 0, width: 800, height: 600 },
  mapOrigin = { x: 0, y: 0 },
  mapSize = { width: 1600, height: 1200 },
  zoom = 1,
  onViewportChange = null,
  showTokens = true,
  showGrid = false,
  gridSize = 50,
  isVisible = true,
  className = ''
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageRevision, setImageRevision] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const geometry = useMemo(() => {
    const width = Math.max(1, finiteOr(mapSize?.width, 1600));
    const height = Math.max(1, finiteOr(mapSize?.height, 1200));
    const origin = {
      x: finiteOr(mapOrigin?.x),
      y: finiteOr(mapOrigin?.y)
    };
    const scale = Math.min(MINIMAP_SIZE.width / width, MINIMAP_SIZE.height / height);
    const contentWidth = width * scale;
    const contentHeight = height * scale;

    return {
      map: { x: origin.x, y: origin.y, width, height },
      scale,
      content: {
        x: (MINIMAP_SIZE.width - contentWidth) / 2,
        y: (MINIMAP_SIZE.height - contentHeight) / 2,
        width: contentWidth,
        height: contentHeight
      }
    };
  }, [mapOrigin?.x, mapOrigin?.y, mapSize?.height, mapSize?.width]);

  const mapToMinimap = useCallback((point) => ({
    x: geometry.content.x + ((finiteOr(point?.x) - geometry.map.x) * geometry.scale),
    y: geometry.content.y + ((finiteOr(point?.y) - geometry.map.y) * geometry.scale)
  }), [geometry]);

  const viewportRect = useMemo(() => {
    const topLeft = mapToMinimap(viewport);
    const bottomRight = mapToMinimap({
      x: finiteOr(viewport?.x) + Math.max(0, finiteOr(viewport?.width)),
      y: finiteOr(viewport?.y) + Math.max(0, finiteOr(viewport?.height))
    });
    const contentRight = geometry.content.x + geometry.content.width;
    const contentBottom = geometry.content.y + geometry.content.height;
    const left = clamp(topLeft.x, geometry.content.x, contentRight);
    const top = clamp(topLeft.y, geometry.content.y, contentBottom);
    const right = clamp(bottomRight.x, geometry.content.x, contentRight);
    const bottom = clamp(bottomRight.y, geometry.content.y, contentBottom);

    return {
      x: Math.min(left, right),
      y: Math.min(top, bottom),
      width: Math.abs(right - left),
      height: Math.abs(bottom - top)
    };
  }, [geometry.content, mapToMinimap, viewport]);

  useEffect(() => {
    imageRef.current = null;
    if (!mapImage) {
      setImageRevision((revision) => revision + 1);
      return undefined;
    }

    let cancelled = false;
    const image = new Image();
    if (mapImage.includes('/uploads/')) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => {
      if (!cancelled) {
        imageRef.current = image;
        setImageRevision((revision) => revision + 1);
      }
    };
    image.onerror = () => {
      if (!cancelled) {
        imageRef.current = null;
        setImageRevision((revision) => revision + 1);
      }
    };
    image.src = mapImage;

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [mapImage]);

  useEffect(() => {
    try {
      const canvas = canvasRef.current;
      const context = canvas?.getContext?.('2d');
      if (!canvas || !context) return;

      const { content, map, scale } = geometry;
      context.clearRect(0, 0, MINIMAP_SIZE.width, MINIMAP_SIZE.height);
      context.fillStyle = '#17110d';
      context.fillRect(0, 0, MINIMAP_SIZE.width, MINIMAP_SIZE.height);

      context.save();
      context.beginPath();
      context.rect(content.x, content.y, content.width, content.height);
      context.clip();

      if (imageRef.current) {
        context.drawImage(imageRef.current, content.x, content.y, content.width, content.height);
      } else {
        context.fillStyle = '#2c1e15';
        context.fillRect(content.x, content.y, content.width, content.height);
      }

      if (showGrid && finiteOr(gridSize) > 0) {
        context.strokeStyle = 'rgba(228, 199, 131, 0.34)';
        context.lineWidth = 0.5;
        const step = finiteOr(gridSize);
        for (let mapX = map.x; mapX <= map.x + map.width; mapX += step) {
          const x = mapToMinimap({ x: mapX, y: map.y }).x;
          context.beginPath();
          context.moveTo(x, content.y);
          context.lineTo(x, content.y + content.height);
          context.stroke();
        }
        for (let mapY = map.y; mapY <= map.y + map.height; mapY += step) {
          const y = mapToMinimap({ x: map.x, y: mapY }).y;
          context.beginPath();
          context.moveTo(content.x, y);
          context.lineTo(content.x + content.width, y);
          context.stroke();
        }
      }

      if (showTokens) {
        tokens.forEach((token) => {
          const point = mapToMinimap(token);
          const tokenWidth = Math.max(1, finiteOr(token?.width, finiteOr(gridSize, 50)));
          const tokenHeight = Math.max(1, finiteOr(token?.height, tokenWidth));
          const center = {
            x: point.x + ((tokenWidth * scale) / 2),
            y: point.y + ((tokenHeight * scale) / 2)
          };
          const radius = Math.max(2.5, Math.min(tokenWidth, tokenHeight) * scale * 0.4);
          context.fillStyle = token?.color || '#d0b071';
          context.beginPath();
          context.arc(center.x, center.y, radius, 0, 2 * Math.PI);
          context.fill();
          context.strokeStyle = '#f5ead3';
          context.lineWidth = 0.75;
          context.stroke();
        });
      }

      if (viewportRect.width > 0 && viewportRect.height > 0) {
        context.fillStyle = 'rgba(196, 157, 92, 0.2)';
        context.fillRect(viewportRect.x, viewportRect.y, viewportRect.width, viewportRect.height);
        context.strokeStyle = '#e4c783';
        context.lineWidth = 2;
        context.setLineDash([4, 3]);
        context.strokeRect(viewportRect.x, viewportRect.y, viewportRect.width, viewportRect.height);
        context.setLineDash([]);
      }

      context.restore();
    } catch (error) {
      handleError(error, false, {
        category: ERROR_CATEGORY.GENERAL,
        message: 'Failed to draw minimap'
      });
    }
  }, [geometry, gridSize, imageRevision, mapToMinimap, showGrid, showTokens, tokens, viewportRect]);

  const moveViewportToMinimapPoint = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || typeof onViewportChange !== 'function') return;
    const bounds = canvas.getBoundingClientRect();
    const canvasX = (clientX - bounds.left) * (MINIMAP_SIZE.width / Math.max(1, bounds.width));
    const canvasY = (clientY - bounds.top) * (MINIMAP_SIZE.height / Math.max(1, bounds.height));
    const containedX = clamp(canvasX, geometry.content.x, geometry.content.x + geometry.content.width);
    const containedY = clamp(canvasY, geometry.content.y, geometry.content.y + geometry.content.height);
    const mapX = geometry.map.x + ((containedX - geometry.content.x) / geometry.scale);
    const mapY = geometry.map.y + ((containedY - geometry.content.y) / geometry.scale);
    const viewportWidth = Math.max(0, finiteOr(viewport?.width));
    const viewportHeight = Math.max(0, finiteOr(viewport?.height));
    const minX = Math.min(geometry.map.x, geometry.map.x + geometry.map.width - viewportWidth);
    const maxX = Math.max(geometry.map.x, geometry.map.x + geometry.map.width - viewportWidth);
    const minY = Math.min(geometry.map.y, geometry.map.y + geometry.map.height - viewportHeight);
    const maxY = Math.max(geometry.map.y, geometry.map.y + geometry.map.height - viewportHeight);

    onViewportChange({
      x: clamp(mapX - (viewportWidth / 2), minX, maxX),
      y: clamp(mapY - (viewportHeight / 2), minY, maxY)
    });
  }, [geometry, onViewportChange, viewport?.height, viewport?.width]);

  const handlePointerDown = useCallback((event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
    moveViewportToMinimapPoint(event.clientX, event.clientY);
  }, [moveViewportToMinimapPoint]);

  const handlePointerMove = useCallback((event) => {
    if (isDragging) {
      moveViewportToMinimapPoint(event.clientX, event.clientY);
    }
  }, [isDragging, moveViewportToMinimapPoint]);

  const handlePointerUp = useCallback((event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setIsDragging(false);
  }, []);

  const handleKeyDown = useCallback((event) => {
    const directions = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1]
    };
    const direction = directions[event.key];
    if (!direction || typeof onViewportChange !== 'function') return;
    event.preventDefault();
    const width = Math.max(0, finiteOr(viewport?.width));
    const height = Math.max(0, finiteOr(viewport?.height));
    const stepX = Math.max(finiteOr(gridSize, 50), width * 0.1);
    const stepY = Math.max(finiteOr(gridSize, 50), height * 0.1);
    const minX = Math.min(geometry.map.x, geometry.map.x + geometry.map.width - width);
    const maxX = Math.max(geometry.map.x, geometry.map.x + geometry.map.width - width);
    const minY = Math.min(geometry.map.y, geometry.map.y + geometry.map.height - height);
    const maxY = Math.max(geometry.map.y, geometry.map.y + geometry.map.height - height);
    onViewportChange({
      x: clamp(finiteOr(viewport?.x) + (direction[0] * stepX), minX, maxX),
      y: clamp(finiteOr(viewport?.y) + (direction[1] * stepY), minY, maxY)
    });
  }, [geometry.map, gridSize, onViewportChange, viewport]);

  if (!isVisible) return null;

  return (
    <section className={`minimap ${className}`} aria-label="Map overview">
      <header className="minimap__header">
        <span className="minimap__title"><MapIcon size={15} aria-hidden="true" /> Minimap</span>
        <span className="minimap__zoom" aria-label={`Current map zoom ${Math.round(zoom * 100)} percent`}>
          {Math.round(zoom * 100)}%
        </span>
      </header>
      <div className="minimap__canvas-frame">
        <canvas
          ref={canvasRef}
          width={MINIMAP_SIZE.width}
          height={MINIMAP_SIZE.height}
          className={`minimap__canvas ${isDragging ? 'is-dragging' : ''}`}
          data-testid="battle-minimap-canvas"
          tabIndex={0}
          role="application"
          aria-label="Map overview. Drag to reposition the camera, or use the arrow keys."
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown}
        />
      </div>
      <footer className="minimap__footer">
        <span>{tokens.length} {tokens.length === 1 ? 'token' : 'tokens'}</span>
        <span>{Math.round(geometry.map.width)}×{Math.round(geometry.map.height)}</span>
      </footer>
    </section>
  );
};

Minimap.propTypes = {
  mapImage: PropTypes.string,
  tokens: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    color: PropTypes.string
  })),
  viewport: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  mapOrigin: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  mapSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  zoom: PropTypes.number,
  onViewportChange: PropTypes.func,
  showTokens: PropTypes.bool,
  showGrid: PropTypes.bool,
  gridSize: PropTypes.number,
  isVisible: PropTypes.bool,
  className: PropTypes.string
};

export default Minimap;
